import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Spinner } from '@components/Spinner/Spinner';
import { useUserBaseInfo } from '@hooks/useUserBaseInfo';
import { Language, StorageKey } from '@shared/enum';
import { localStorageService } from '@shared/localStorageService';
import { useAppDispatch, useAppSelector } from '@state/configureStore';
import {
    selectHideLoader,
    selectIsAuthenticated,
    selectIsLoading,
    setBaseInfo,
    setIsAuthenticated,
    setIsTournamentRoute,
} from '@state/pageSlice';
import { UserRole } from '@state/userApi';
import './App.scss';
import { AppRoutes } from './AppRoutes';
import { Footer } from './Footer/Footer';
import { LauncherUpdateBanner } from './LauncherUpdateBanner';
import { Menu } from './Menu/Menu';
import { ScrollToTop } from './ScrollToTop';

export const App: FC = () => {
    const sectionRef = useRef<HTMLElement>(null);
    const isLoading = useAppSelector(selectIsLoading);
    const hideLoader = useAppSelector(selectHideLoader);
    const isAuthenticated = useAppSelector(selectIsAuthenticated);
    const { t } = useTranslation();
    const [fetchUserInfo] = useUserBaseInfo();
    const dispatch = useAppDispatch();
    const [isHydrated, setIsHydrated] = useState(false);
    const location = useLocation();
    const isTournamentRoute = location.pathname.startsWith('/tournament/');

    useEffect(() => {
        dispatch(setIsTournamentRoute(isTournamentRoute));
    }, [dispatch, isTournamentRoute]);

    useEffect(() => {
        if (isAuthenticated && isHydrated) {
            fetchUserInfo();
        }
    }, [t, fetchUserInfo, isAuthenticated, isHydrated]);

    useEffect(() => {
        document.title = t('title.main');
        if (!isHydrated) {
            const isAuthenticated = localStorageService.getItem(StorageKey.isAuthenticated);
            if (typeof isAuthenticated !== 'undefined') {
                dispatch(setIsAuthenticated(isAuthenticated === '1'));
            }

            const storageData = localStorageService.getItem(StorageKey.baseInfo);
            if (storageData) {
                const converted: {
                    fullname: string;
                    language: Language;
                    userRole?: UserRole;
                } = JSON.parse(storageData as string);
                if (converted.fullname && converted.language) {
                    dispatch(
                        setBaseInfo({
                            fullname: converted.fullname,
                            language: converted.language,
                            userRole: converted.userRole,
                        }),
                    );
                }
            }

            setIsHydrated(true);
        }
    }, [t, dispatch, isHydrated]);

    if (!isHydrated) {
        return <Spinner hideLoader={hideLoader} />;
    }

    return (
        <>
            <ScrollToTop />
            {isLoading && <Spinner hideLoader={hideLoader} />}
            {!isTournamentRoute && <Menu />}
            <main
                className={`real-content ${isTournamentRoute ? 'tournament-content' : ''}`}
                ref={sectionRef as React.RefObject<HTMLElement>}
            >
                {!isTournamentRoute && <LauncherUpdateBanner />}
                <AppRoutes />
            </main>
            {!isTournamentRoute && <Footer />}
        </>
    );
};
